/**
 * src/utils/conversationApi.js
 * Pure, thin client-side API helper for handling all conversational interactions.
 *
 * ── STREAM TRACE INSTRUMENTATION ────────────────────────────────────────────
 * Every stage emits a console.log prefixed [TRACE:stream].
 * Search DevTools for "[TRACE:stream]" to filter the full timeline.
 * ─────────────────────────────────────────────────────────────────────────────
 */

function ts(t0) {
  return `+${Date.now() - t0}ms`;
}

export async function streamChatTurn({
  authToken,
  reply,
  sessionId,
  workspaceCtx,
  onThinking,
  onChunk,
  onReplace,
  onDone,
  onError,
  onPipelineEvent,
  onPreviewUpdate,   // Preview Live Sync: called when backend signals a section was saved
}) {
  const controller = new AbortController();
  const t0 = Date.now();
  let chunkCount = 0;
  let thinkingCount = 0;
  let doneReceived = false;
  let streamClosed = false;

  console.log(`[TRACE:stream] ── REQUEST START ── ${new Date().toISOString()}`);
  console.log(`[TRACE:stream] sessionId=${sessionId} | docId=${workspaceCtx?.document_id} | workspaceId=${workspaceCtx?.workspace_id}`);
  console.log(`[TRACE:stream] reply length=${reply?.length ?? 0} messages`);

  // Internal helper: parse and dispatch a single SSE frame string
  function dispatchFrame(rawEvent, accumulatedText) {
    const dataLines = rawEvent.split("\n").filter((l) => l.startsWith("data: "));
    if (dataLines.length === 0) return accumulatedText;

    const line = dataLines[dataLines.length - 1];
    let eventData;
    try {
      eventData = JSON.parse(line.slice(6));
    } catch {
      console.warn(`[TRACE:stream] ${ts(t0)} PARSE ERROR — raw frame: ${rawEvent.slice(0, 200)}`);
      return accumulatedText;
    }

    switch (eventData.type) {
      case "thinking":
        thinkingCount++;
        console.log(`[TRACE:stream] ${ts(t0)} THINKING #${thinkingCount}: "${eventData.message}"`);
        if (onThinking) onThinking(eventData.message || "");
        break;

      case "chunk":
        chunkCount++;
        accumulatedText += eventData.content;
        if (chunkCount === 1) {
          console.log(`[TRACE:stream] ${ts(t0)} FIRST CHUNK received`);
        }
        if (chunkCount % 20 === 0) {
          console.log(`[TRACE:stream] ${ts(t0)} chunk #${chunkCount} | accumulated=${accumulatedText.length} chars`);
        }
        if (onChunk) onChunk(eventData.content, accumulatedText);
        break;

      case "replace":
        accumulatedText = eventData.content;
        console.log(`[TRACE:stream] ${ts(t0)} REPLACE event | length=${eventData.content?.length ?? 0} chars`);
        if (onReplace) onReplace(eventData.content);
        break;

      case "done":
        doneReceived = true;
        console.log(`[TRACE:stream] ${ts(t0)} ── DONE EVENT RECEIVED ──`);
        console.log(`[TRACE:stream]   .success    = ${eventData.success}`);
        console.log(`[TRACE:stream]   .resume     = ${eventData.resume ? 'PRESENT' : 'MISSING'}`);
        console.log(`[TRACE:stream]   .resume_data= ${eventData.resume_data ? 'PRESENT' : 'MISSING'}`);
        console.log(`[TRACE:stream]   .ats        = ${eventData.ats ? JSON.stringify(eventData.ats).slice(0,120) : 'MISSING'}`);
        console.log(`[TRACE:stream]   .conversation= ${eventData.conversation ? (typeof eventData.conversation === 'object' ? `object with keys: [${Object.keys(eventData.conversation).join(', ')}]` : typeof eventData.conversation) : 'MISSING'}`);
        console.log(`[TRACE:stream]   .session_messages= ${eventData.session_messages ? `array[${eventData.session_messages.length}]` : 'MISSING'}`);
        console.log(`[TRACE:stream]   .warnings   = ${JSON.stringify(eventData.warnings ?? []).slice(0,120)}`);
        console.log(`[TRACE:stream]   full payload keys: [${Object.keys(eventData).join(', ')}]`);
        if (onDone) {
          console.log(`[TRACE:stream] ${ts(t0)} calling onDone()…`);
          onDone(eventData);
          console.log(`[TRACE:stream] ${ts(t0)} onDone() returned`);
        } else {
          console.error(`[TRACE:stream] ${ts(t0)} !! onDone is undefined — UI WILL FREEZE !!`);
        }
        break;

      case "pipeline_event":
        console.log(`[TRACE:stream] ${ts(t0)} PIPELINE EVENT: "${eventData.stage}" status: "${eventData.status}"`);
        if (onPipelineEvent) onPipelineEvent(eventData);
        break;

      case "error":
        console.error(`[TRACE:stream] ${ts(t0)} ERROR event: "${eventData.message}"`);
        if (onError) onError(eventData.message || "An error occurred");
        break;

      case "preview_update":
        // Preview Live Sync: backend saved a section; re-fetch document and update canvas
        console.log(`[TRACE:stream] ${ts(t0)} PREVIEW_UPDATE — section=${eventData.section || 'unknown'}`);
        if (onPreviewUpdate) onPreviewUpdate(eventData);
        break;

      default:
        // Forward payloads that look like done events (backend shape variants)
        if (eventData.resume_data || eventData.session_messages) {
          doneReceived = true;
          console.log(`[TRACE:stream] ${ts(t0)} implicit DONE via payload shape (type="${eventData.type}")`);
          console.log(`[TRACE:stream]   payload keys: [${Object.keys(eventData).join(', ')}]`);
          if (onDone) onDone(eventData);
        } else {
          console.log(`[TRACE:stream] ${ts(t0)} UNKNOWN event type="${eventData.type}" — ignored`);
        }
        break;
    }
    return accumulatedText;
  }

  try {
    const response = await fetch("/api/chat/stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        reply,
        session_id: sessionId,
        workspace_ctx: workspaceCtx,
      }),
      signal: controller.signal,
    });

    console.log(`[TRACE:stream] ${ts(t0)} HTTP CONNECTED — status=${response.status} ${response.statusText}`);
    console.log(`[TRACE:stream]   Content-Type: ${response.headers.get('content-type')}`);

    if (!response.ok) {
      console.error(`[TRACE:stream] ${ts(t0)} HTTP ERROR ${response.status} — aborting`);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let accumulatedText = "";
    let readCount = 0;

    console.log(`[TRACE:stream] ${ts(t0)} STREAM READER opened`);

    while (true) {
      const { done, value } = await reader.read();
      readCount++;

      // Flush decoder and buffer BEFORE breaking on stream EOF
      if (done) {
        streamClosed = true;
        const tail = decoder.decode(undefined, { stream: false });
        if (tail) {
          console.log(`[TRACE:stream] ${ts(t0)} EOF — decoder tail flushed (${tail.length} bytes)`);
          buffer += tail;
        }

        // Process any complete SSE frames left in buffer
        if (buffer.trim()) {
          const finalEvents = buffer.split("\n\n");
          console.log(`[TRACE:stream] ${ts(t0)} EOF — flushing buffer: ${finalEvents.length} frame(s) remaining`);
          for (const rawEvent of finalEvents) {
            if (rawEvent.trim()) {
              accumulatedText = dispatchFrame(rawEvent, accumulatedText);
            }
          }
        } else {
          console.log(`[TRACE:stream] ${ts(t0)} EOF — buffer empty at close`);
        }

        console.log(`[TRACE:stream] ${ts(t0)} ── STREAM CLOSED ──`);
        console.log(`[TRACE:stream]   total reads    = ${readCount}`);
        console.log(`[TRACE:stream]   thinking events= ${thinkingCount}`);
        console.log(`[TRACE:stream]   chunk events   = ${chunkCount}`);
        console.log(`[TRACE:stream]   DONE received  = ${doneReceived}`);
        if (!doneReceived) {
          console.error(`[TRACE:stream] !! DONE NEVER RECEIVED — stream ended without DONE !!`);
          console.error(`[TRACE:stream]    This will leave isStreaming=true and freeze the UI.`);
          console.error(`[TRACE:stream]    Timestamp: ${new Date().toISOString()}`);
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const rawEvents = buffer.split("\n\n");
      buffer = rawEvents.pop() || "";

      for (const rawEvent of rawEvents) {
        accumulatedText = dispatchFrame(rawEvent, accumulatedText);
      }
    }
  } catch (err) {
    if (err.name !== "AbortError" && onError) {
      console.error(`[TRACE:stream] ${ts(t0)} CATCH ERROR: ${err.name} — ${err.message}`);
      console.error(`[TRACE:stream]   doneReceived=${doneReceived} | streamClosed=${streamClosed}`);
      onError(err.message || "Network error");
    } else if (err.name === "AbortError") {
      console.log(`[TRACE:stream] ${ts(t0)} stream aborted by user`);
    }
  }

  return controller;
}

export async function dispatchDocumentOperation({
  authToken,
  operation,
  activeDocument,
}) {
  const response = await fetch("/api/document/operation", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      operation,
      active_document: activeDocument,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchSessionContext({
  authToken,
  docType,
  docId,
  isMaster,
  entryIntent,
}) {
  const body = { is_master: isMaster, entry_intent: entryIntent };
  if (docType === "cover_letter") {
    body.cover_letter_id = docId;  // legacy compat field, still read by the old session lookup
    // Document-First fields — without these the backend defaults document_type
    // to "resume" before it ever considers cover_letter_id, which sends a
    // brand-new cover letter's first turn through the resume brain instead
    // of the Cover Letter Coach.
    body.document_ctx = { id: docId, type: docType, entry_intent: entryIntent };
    body.document_id = docId;
    body.document_type = docType;
  } else if (docType === "resume_variant" || docType === "variant") {
    // DEPRECATED legacy path — old ResumeVariant rows
    body.resume_variant_id = docId;
  } else if (docType === "resume" || docType === "master" || docType === "master_resume") {
    // Document-First path — new polymorphic resume documents.
    // "master" and "master_resume" are the same document as "resume" here —
    // just labeled differently depending on the caller (route mount vs.
    // import flow). All three must resolve to the same session lookup, or a
    // plain page refresh finds a different (often stale/legacy) session than
    // the one the import flow just created.
    body.document_ctx = { id: docId, type: docType, entry_intent: entryIntent };
    body.document_id = docId;
    body.document_type = docType;
  }

  const response = await fetch("/api/chat/session/context", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}
